/**
 * Typed GTM `dataLayer` event wrappers.
 *
 * Ported verbatim from the winnin-auth MFE (`KeycloakWrapper/index.tsx`).
 * Each function wraps exactly one guarded `window.dataLayer.push(...)`.
 *
 * Pure functions only — no React, no side effects beyond the guarded push.
 *
 * Intended call order (sequencing/dedup is owned by the `me` query's `onCompleted`):
 *   on ME success:  userAuthenticated(user)
 *                   notWinninUserIsAuthenticated()  // only if email is set and not @winnin.com
 *
 * Both feed the `Survicate` tag downstream, which is how NPS is collected.
 *
 * `dataLayer` is read from `globalThis` cast to `TGtmGlobal` (typed in `./gtm.types.ts`),
 * so there is no `any` and no global ambient declaration.
 */

import type { TGtmAttributeElement, TGtmAttributeMap, TGtmGlobal, TGtmUser } from './gtm.types'

const getDataLayer = (): Record<string, unknown>[] | undefined => (globalThis as TGtmGlobal).dataLayer

export const userAuthenticated = (user: TGtmUser) => {
  const dataLayer = getDataLayer()
  if (dataLayer) {
    const attributesHubspot: TGtmAttributeMap = {}
    user?.attributes?.hubspot?.forEach((element: TGtmAttributeElement) => {
      attributesHubspot[element.key] = element.value
    })

    dataLayer.push({
      event: 'userIsAuthenticated',
      email: user?.email,
      id: user?.id,
      role: user?.role,
      name: user?.name,
      companyName: user?.role === 25 ? 'Guest' : user?.company?.name,
      companyType: user?.company?.companyType?.name,
      jobTitle: user?.jobTitleType?.name,
      country: user?.country,
      contractPlan: user?.company?.plan?.name,
      releaseType: user?.releaseType,
      companyId: user?.company?.id,
      createdAt: user?.createdAt,
      shouldShowNPS: attributesHubspot?.should_show_nps,
    })
  }
}

export const notWinninUserIsAuthenticated = () => {
  const dataLayer = getDataLayer()
  if (dataLayer) {
    dataLayer.push({
      event: 'notWinninUserIsAuthenticated',
    })
  }
}
